import { Icon as QIcon, Flex, Text } from '@sendoutcards/quantum-design-ui'
import { motion } from 'motion/react'
import React from 'react'
import { getReadableTextColor } from 'src/helpers'

type ExternalTogglePillProps = {
  open: boolean
  onClick: () => void
  openLabel: string
  closedLabel: string
  anchorRef?: React.Ref<HTMLDivElement>
  backgroundColor?: string
  showChevronBurst?: boolean
  chevronBurstPlacement?: 'above' | 'below'
  chevronBurstDirection?: 'up' | 'down'
}

export const ExternalTogglePill: React.FC<ExternalTogglePillProps> = ({
  open,
  onClick,
  openLabel,
  closedLabel,
  anchorRef,
  backgroundColor,
  showChevronBurst = false,
  chevronBurstPlacement = 'above',
  chevronBurstDirection = 'up',
}) => {
  const pillTextColor = getReadableTextColor(backgroundColor)

  const ChevronBurst = ({
    placement,
    direction,
  }: {
    placement: 'above' | 'below'
    direction: 'up' | 'down'
  }) => {
    const isBelow = placement === 'below'
    const burstOffset = isBelow ? 10 : -18
    const chevronRotation = direction === 'down' ? '90deg' : '-90deg'
    const yAnimation = direction === 'down' ? [0, -8, 0] : [0, 8, 0]

    return (
      <Flex
        alignItems="center"
        justifyContent="center"
        style={{
          position: 'absolute',
          top: isBelow ? '100%' : 0,
          left: '50%',
          transform: `translate(-50%, ${burstOffset}px)`,
          pointerEvents: 'none',
          gap: 6,
        }}
      >
        {[0, 1, 2].map(idx => (
          <motion.div
            key={idx}
            animate={{ y: yAnimation }}
            transition={{
              repeat: Infinity,
              duration: 0.8,
              delay: idx * 0.08,
              ease: 'easeInOut',
            }}
          >
            <div style={{ transform: `rotate(${chevronRotation})` }}>
              <QIcon
                size="small"
                name="chevron"
                primaryColor="secondaryBrand"
              />
            </div>
          </motion.div>
        ))}
      </Flex>
    )
  }

  return (
    <div
      ref={anchorRef}
      style={{ position: 'relative', display: 'inline-flex', paddingTop: 12 }}
    >
      {showChevronBurst && (
        <ChevronBurst
          placement={chevronBurstPlacement}
          direction={chevronBurstDirection}
        />
      )}
      <Flex
        onClick={onClick}
        cursor="pointer"
        inset={{ horizontal: 'x1', vertical: 'x_5' }}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          columnGap: 8,
          minWidth: 240,
          padding: '4px 12px',
          background: backgroundColor
            ? backgroundColor
            : 'rgba(64, 64, 64, 0.05)',
          borderRadius: 16,
          color: pillTextColor,
          fontFamily: 'inherit',
          fontWeight: 600,
        }}
      >
        <Flex alignItems="center" justifyContent="center" width="100%">
          <Text
            type="caption"
            weight="semiBold"
            style={{ color: pillTextColor }}
          >
            {open ? openLabel : closedLabel}
          </Text>
        </Flex>
        <Flex
          alignItems="center"
          justifyContent="center"
          style={{
            transform: open ? 'rotate(90deg)' : 'rotate(-90deg)',
            transition: 'transform 180ms ease',
          }}
        >
          <QIcon size="small" name="chevron" primaryColor={pillTextColor} />
        </Flex>
      </Flex>
    </div>
  )
}
